import { ChartDataOptions, TableDataOptions } from '@sisense/sdk-ui';
import {
	AxesMapping,
	getMinimalChartStyleOptions,
	translateChartToJSON,
} from '@sisense/sdk-ui/analytics-composer';
import {
	BuildChartContext,
	createChartSchema,
	ExtendedChartWidgetProps,
	NullableChartType,
} from '../types';
import type { QueryState } from '@/services/nlq/nlq-service';
import { nanoidHex } from '@/utils/id-utils';
import { devDebug } from '@/utils/dev-debug';
import { generateChartRecommendations } from './generate-chart-recommendations';
import {
	generateChartTable,
	loadChartTableFromQueryId,
} from './generate-chart-table';

import z from 'zod';

const VERBATIM_QUERY_REQUIRES_V3 =
	'Verbatim chart-from-query requires NLQ V3 (isNlqV3Enabled). Enable V3 or omit queryId and describe the data in the prompt instead.';

const VERBATIM_QUERY_REQUIRES_RETRIEVE_QUERY =
	'Verbatim chart-from-query requires retrieveQuery context function. Wire retrieveQuery or omit queryId and describe the data in the prompt instead.';

type GenerateChartParams = {
	dataSourceTitle: string;
	queryPrompt: string;
	chartPrompt: string;
	chartType: NullableChartType;
	chartTitle: string;
	/** Skip data fetch — load this exact prior result verbatim when this id is passed (e.g. from a prior buildQuery call). Takes priority over priorQueryState. Requires NLQ V3. */
	queryId?: string | null;
	/** Fetch fresh data, but carry this state forward as incremental context (from updateChartQuery structural revision). Ignored when queryId is set. */
	priorQueryState?: QueryState;
};

type GenerateChartOutput = {
	widgetProps: ExtendedChartWidgetProps;
};

export const generateChart = async (
	{
		dataSourceTitle,
		queryPrompt,
		chartPrompt,
		chartType,
		chartTitle,
		queryId,
		priorQueryState,
	}: GenerateChartParams,
	context: BuildChartContext,
): Promise<GenerateChartOutput> => {
	devDebug('toolCall', {
		tool: 'generateChart',
		dataSourceTitle,
		queryPrompt,
		chartPrompt,
		chartType,
		chartTitle,
		queryId,
		priorQueryState,
	});
	let widgetProps: ExtendedChartWidgetProps;

	if (queryId != null && queryId !== '') {
		if (context.retrieveQuery == null) {
			throw new Error(VERBATIM_QUERY_REQUIRES_RETRIEVE_QUERY);
		}
		if (!context.isNlqV3Enabled) {
			throw new Error(VERBATIM_QUERY_REQUIRES_V3);
		}
		const { widgetProps: loaded } = await loadChartTableFromQueryId(
			queryId,
			context.retrieveQuery,
			queryPrompt,
		);
		widgetProps = loaded;
	} else {
		const { widgetProps: generated } = await generateChartTable(
			dataSourceTitle,
			queryPrompt,
			context.isNlqV3Enabled ?? false,
			context.httpClient,
			context.commonFilters,
			priorQueryState,
			context.isNlqDeltaEnabled,
			context.retrieveQuery,
		);
		widgetProps = generated;
	}

	const dataColumns = (widgetProps.dataOptions as TableDataOptions).columns;

	const chartRecommendations = await generateChartRecommendations(
		chartPrompt,
		dataColumns,
		chartType!,
		context.openAIClient,
	);

	const { axesMapping, chartType: newChartType } = chartRecommendations;

	widgetProps.dataOptions = axesMapping as ChartDataOptions;
	widgetProps.chartType = newChartType;
	// Preserve NLQ title from table phase when available; otherwise use chartTitle param
	widgetProps.title = widgetProps.title || chartTitle;

	const baseStyleOptions = getMinimalChartStyleOptions(
		newChartType,
		widgetProps.dataOptions as AxesMapping,
	);

	widgetProps.styleOptions = baseStyleOptions;

	return {
		widgetProps: { ...widgetProps, artifactId: nanoidHex(), dataColumns },
	};
};

export const createChart = async (
	{
		queryPrompt,
		chartPrompt,
		chartType,
		chartTitle,
		queryId,
	}: z.infer<typeof createChartSchema>,
	context: BuildChartContext,
) => {
	devDebug('toolCall', {
		tool: 'createChart',
		queryPrompt,
		chartPrompt,
		chartType,
		chartTitle,
		queryId,
	});
	const dataSourceTitle = context.dataSourceTitle;
	const chartId = context.toolCallId;
	const { widgetProps } = await generateChart(
		{
			dataSourceTitle,
			queryPrompt,
			chartPrompt,
			chartType,
			chartTitle,
			queryId,
		},
		context,
	);

	const chartTranslation = translateChartToJSON(widgetProps);
	if (!chartTranslation.success) {
		throw new Error(
			'translateChartToJSON failed: chart could not be serialized. Check chart type, axes mapping, and data options.',
		);
	}
	widgetProps.chartState = {
		...chartTranslation.data,
		...(widgetProps.title && { title: widgetProps.title }),
	};

	context.saveChart?.(chartId, widgetProps);

	return {
		message: `New chart (chartId: ${chartId}) created successfully`,
		chartState: widgetProps.chartState,
		dataset: widgetProps.dataset,
	};
};
